/**
 * useLossRuns — shared, staged CRUD hook over a submission's Loss Runs
 * claim list.
 *
 * Mirrors `useFacilities`: the Loss Runs page and any other consumer
 * subscribe through this hook to the same submission-scoped list via a
 * module-level pubsub keyed by `submissionId`, so an edit in one place
 * instantly re-renders the others.
 *
 * Data source: the real backend (`/v2/submissions/{id}/loss-runs/`,
 * jun-16). The list response carries the card aggregates and the
 * `/summary` endpoint carries the per-year breakdown (Annual Loss
 * Summary + Loss Ratio Analysis tabs) — both backend-computed.
 *
 * Staged editing (matches the other submission sections):
 *   - `create` / `update` / `remove` mutate ONLY the in-memory working
 *     copy — nothing hits the network.
 *   - `save()` diffs the working copy against the last-saved baseline
 *     and flushes every change (POST new rows, PATCH changed rows,
 *     DELETE removed rows), then re-lists the rows (fresh per-row
 *     `sir_by_community` + card aggregates) and refreshes the summary.
 *     The Loss Runs page's Save button calls it.
 *   - `isDirty` reports whether there are unsaved changes; `dirtyIds`
 *     is the per-row set so calculated columns render "—" until saved.
 *
 * State machine:
 *   { status: "loading" }
 *   → { status: "ready", data: LossRunRow[] }
 *   → { status: "error", error: Error }
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
    createLossRun as createLossRunApi,
    deleteLossRun as deleteLossRunApi,
    getLossRunSummary as getLossRunSummaryApi,
    listLossRuns as listLossRunsApi,
    patchLossRun as patchLossRunApi,
} from "../api";
import type { LossRunLocationRef } from "../api";
import type { LossRunAggregates, LossRunRow, LossRunStagePayload, LossRunSummary, LossRunWritePayload } from "../types/loss-run";
import { emptyLossRun } from "../types/loss-run";

const EMPTY_SUMMARY: LossRunSummary = {
    totalPaidAndReserved: "",
    totalExcess: "",
    totalAdjustedLossRatio: "",
    yearsWithLosses: null,
    totalEquivalency: "",
    unadjustedRatePerBed: "",
    premium: "",
    totalPremium: "",
    rows: [],
};

export type LossRunsQueryState =
    | { status: "loading" }
    | { status: "ready"; data: LossRunRow[]; aggregates: LossRunAggregates; locations: LossRunLocationRef[]; summary: LossRunSummary }
    | { status: "error"; error: Error };

export interface UseLossRunsResult {
    state: LossRunsQueryState;
    /** Stage a new row locally (temp id). Not persisted until `save()`. */
    create: (payload?: LossRunWritePayload) => Promise<LossRunRow | null>;
    /** Stage a field change locally. Not persisted until `save()`. */
    update: (id: string, payload: LossRunStagePayload) => Promise<void>;
    /** Stage a row removal locally. Not persisted until `save()`. */
    remove: (id: string) => Promise<void>;
    /** Flush staged changes (create / update / delete) to the backend.
     *  Returns false if any call failed. Pass `savable` to persist only the
     *  rows it approves (auto-save: skips half-filled rows without blocking
     *  the valid ones); omit it to flush everything (Save & Continue). */
    save: (opts?: { savable?: (row: LossRunRow) => boolean }) => Promise<boolean>;
    /** True when the working copy differs from the last-saved state. */
    isDirty: boolean;
    /** Row ids with unsaved changes (new rows + edited rows). The table
     *  renders backend-calculated columns as "—" for these until Save. */
    dirtyIds: Set<string>;
}

const DRAFT_SCOPE = "draft";
/** Prefix for client-side ids on rows that haven't been POSTed yet. */
const TEMP_ID_PREFIX = "local-";

/** Writable columns. `emptyLossRun()` enumerates exactly the
 *  `LossRunWritePayload` keys, so it doubles as the canonical list. */
const WRITABLE_KEYS = Object.keys(emptyLossRun()) as (keyof LossRunWritePayload)[];

// ── Module-level pubsub ──────────────────────────────────────────
// One state slot per submission scope. All hook instances pointing at
// the same scope share these slots, so an update in one consumer
// re-renders the others.

const stateByKey = new Map<string, LossRunsQueryState>();
/** Last-saved server snapshot per scope — the diff baseline for `save()`. */
const baselineByKey = new Map<string, LossRunRow[]>();
const subscribersByKey = new Map<string, Set<(s: LossRunsQueryState) => void>>();
const inflightLoad = new Map<string, Promise<void>>();
/** In-flight save chain per scope. The debounced autosave and the manual
 *  Save button both call `save()`; chaining serializes them so two flushes
 *  never interleave (double-PATCHing rows and racing each other's final
 *  state emit). */
const saveChainByKey = new Map<string, Promise<boolean>>();

function scopeOf(submissionId: string): string {
    return submissionId.trim() || DRAFT_SCOPE;
}

function isTempId(id: string): boolean {
    return id.startsWith(TEMP_ID_PREFIX);
}

/** Shallow per-row clone — `LossRunRow` is a flat string record, so this
 *  is enough to detach the baseline from the working copy. */
function snapshot(rows: LossRunRow[]): LossRunRow[] {
    return rows.map((r) => ({ ...r }));
}

function sameData(a: LossRunRow[], b: LossRunRow[]): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
}

/** Full editable payload for a brand-new row (POST body). */
function toWritePayload(r: LossRunRow): LossRunWritePayload {
    const out: Record<string, unknown> = {};
    for (const k of WRITABLE_KEYS) out[k] = r[k];
    return out as LossRunWritePayload;
}

/** Only the columns that changed vs the baseline (PATCH body). */
function diffPayload(base: LossRunRow, cur: LossRunRow): LossRunWritePayload {
    const diff: Record<string, unknown> = {};
    for (const k of WRITABLE_KEYS) {
        if (base[k] !== cur[k]) diff[k] = cur[k];
    }
    return diff as LossRunWritePayload;
}

function emit(key: string, next: LossRunsQueryState): void {
    stateByKey.set(key, next);
    const subs = subscribersByKey.get(key);
    if (!subs) return;
    subs.forEach((cb) => cb(next));
}

function subscribe(key: string, cb: (s: LossRunsQueryState) => void): () => void {
    let set = subscribersByKey.get(key);
    if (!set) {
        set = new Set();
        subscribersByKey.set(key, set);
    }
    set.add(cb);
    return () => {
        set!.delete(cb);
    };
}

/** Replace the data array in place via a transform — preserves the
 *  "ready" status wrapper and emits to subscribers. No-op when the
 *  scope hasn't loaded yet. */
function mutateData(key: string, transform: (prev: LossRunRow[]) => LossRunRow[]): void {
    const current = stateByKey.get(key);
    if (current?.status !== "ready") return;
    // Spread preserves `aggregates` and `locations`.
    emit(key, { ...current, data: transform(current.data) });
}

function load(submissionId: string): Promise<void> {
    const key = scopeOf(submissionId);
    const existing = inflightLoad.get(key);
    if (existing) return existing;
    const promise = Promise.all([
        listLossRunsApi(submissionId),
        // Summary feeds the Annual + Ratio tabs; tolerate its absence so a
        // summary outage doesn't block the editable table.
        getLossRunSummaryApi(submissionId).catch(() => EMPTY_SUMMARY),
    ])
        .then(([{ rows, aggregates, locations }, summary]) => {
            // The freshly-loaded list is both the working copy and the
            // baseline `save()` diffs against.
            baselineByKey.set(key, snapshot(rows));
            emit(key, { status: "ready", data: rows, aggregates, locations, summary });
        })
        .catch((error: unknown) => {
            emit(key, {
                status: "error",
                error: error instanceof Error ? error : new Error("Failed to load loss runs"),
            });
        })
        .finally(() => {
            inflightLoad.delete(key);
        });
    inflightLoad.set(key, promise);
    return promise;
}

/** Background refresh for a scope that may already be cached. The Loss
 *  Runs page calls this on mount so values another section changed since
 *  the first load land without a hard refresh — e.g. editing a facility's
 *  Retention in Schedule recomputes every matched row's `sir_by_community`
 *  (OR-1337), but nothing else re-fetches this store.
 *
 *  - Nothing loaded yet → plain `load()` (deduped via `inflightLoad`).
 *  - Cached & clean → adopt the fresh server list wholesale (new baseline).
 *  - Cached & dirty → keep the staged working copy and adopt only the
 *    server-owned display fields (`sirByCommunity`, `totalIncurred`) into
 *    the live rows AND the baseline — neither is a writable key, so staged
 *    edits and dirtiness are untouched.
 *
 *  Chained behind the scope's save chain so it never merges against rows
 *  a concurrent flush is still PATCHing. Failures are swallowed — the
 *  cached state simply stays up. */
export function refreshLossRuns(submissionId: string): Promise<void> {
    const key = scopeOf(submissionId);
    if (stateByKey.get(key)?.status !== "ready") return load(submissionId);
    const existing = inflightLoad.get(key);
    if (existing) return existing;

    const run = (): Promise<void> =>
        Promise.all([
            listLossRunsApi(submissionId),
            getLossRunSummaryApi(submissionId).catch(() => EMPTY_SUMMARY),
        ]).then(([{ rows, aggregates, locations }, summary]) => {
            const live = stateByKey.get(key);
            if (live?.status !== "ready") return;
            const baseline = baselineByKey.get(key) ?? [];
            if (sameData(live.data, baseline)) {
                baselineByKey.set(key, snapshot(rows));
                emit(key, { status: "ready", data: rows, aggregates, locations, summary });
                return;
            }
            const serverById = new Map(rows.map((r) => [r.id, r]));
            const adopt = (r: LossRunRow): LossRunRow => {
                const server = serverById.get(r.id);
                return server ? { ...r, sirByCommunity: server.sirByCommunity, totalIncurred: server.totalIncurred } : r;
            };
            baselineByKey.set(key, baseline.map(adopt));
            emit(key, { status: "ready", data: live.data.map(adopt), aggregates, locations, summary });
        });

    const prevSave = saveChainByKey.get(key) ?? Promise.resolve(true);
    const promise = prevSave
        .then(run, run)
        .catch(() => undefined)
        .finally(() => {
            inflightLoad.delete(key);
        });
    inflightLoad.set(key, promise);
    return promise;
}

// ── Hook ─────────────────────────────────────────────────────────

export function useLossRuns(submissionId: string | undefined): UseLossRunsResult {
    const key = submissionId === undefined ? null : scopeOf(submissionId);

    const [state, setState] = useState<LossRunsQueryState>(() => {
        if (key === null) return { status: "loading" };
        return stateByKey.get(key) ?? { status: "loading" };
    });

    useEffect(() => {
        if (key === null || submissionId === undefined) return;
        // Subscribe FIRST so we don't miss the resolution of an
        // already-in-flight load triggered by a sibling hook.
        const unsubscribe = subscribe(key, setState);
        // Seed local state from cache if present.
        const cached = stateByKey.get(key);
        if (cached) setState(cached);
        // Trigger a load if we haven't yet (or the last attempt errored).
        if (!cached || cached.status === "error") {
            emit(key, { status: "loading" });
            void load(submissionId);
        }
        return unsubscribe;
    }, [key, submissionId]);

    // ── Staged mutations — local only, no network ────────────
    const create = useCallback<UseLossRunsResult["create"]>(
        async (payload = {}) => {
            if (submissionId === undefined || key === null) return null;
            const row = {
                ...emptyLossRun(),
                // operator is read-only/derived (OR-1370) — seed blank on a new
                // row; it fills once a Location is linked and the row reloads.
                operator: "",
                ...payload,
                id: `${TEMP_ID_PREFIX}${crypto.randomUUID()}`,
                submissionId,
                createdAt: "",
                updatedAt: "",
            } as LossRunRow;
            mutateData(key, (prev) => [...prev, row]);
            return row;
        },
        [submissionId, key],
    );

    const update = useCallback<UseLossRunsResult["update"]>(
        async (id, payload) => {
            if (key === null) return;
            mutateData(key, (prev) => prev.map((r) => (r.id === id ? { ...r, ...payload } : r)));
        },
        [key],
    );

    const remove = useCallback<UseLossRunsResult["remove"]>(
        async (id) => {
            if (key === null) return;
            mutateData(key, (prev) => prev.filter((r) => r.id !== id));
        },
        [key],
    );

    // ── Flush staged changes to the backend ──────────────────
    const save = useCallback<UseLossRunsResult["save"]>(async (opts) => {
        if (submissionId === undefined || key === null) return false;

        const run = async (): Promise<boolean> => {
            const current = stateByKey.get(key);
            if (current?.status !== "ready") return true; // nothing loaded → nothing to save
            const working = current.data;
            const { locations } = current;
            const baseline = baselineByKey.get(key) ?? [];
            if (sameData(working, baseline)) return true; // clean

            // Auto-save passes `savable` so only valid rows are persisted: a
            // half-filled new row is left staged (no 422) WITHOUT blocking the
            // valid rows around it. Save & Continue omits it (rows pre-validated).
            const savable = opts?.savable;

            try {
                // Deletes: baseline rows no longer in the working copy.
                // (Temp-id rows are never in the baseline, so a created-then-
                // removed row simply vanishes with no API call.)
                const deletes = baseline.filter((b) => !working.some((w) => w.id === b.id));
                await Promise.all(deletes.map((d) => deleteLossRunApi(submissionId, d.id)));

                // Creates / updates, preserving row order. The server returns
                // the recomputed `sir` / `total_incurred`, so adopt those too.
                // `nextBaseline` tracks only what's actually persisted, so skipped
                // rows stay dirty and get retried once they become savable.
                // `savedByWorkingId` maps each working row to its saved form for
                // the merge below (created rows keyed by their temp id).
                const nextBaseline: LossRunRow[] = [];
                const savedByWorkingId = new Map<string, LossRunRow>();
                for (const row of working) {
                    const base = isTempId(row.id) ? undefined : baseline.find((b) => b.id === row.id);
                    if (savable && !savable(row)) {
                        // Not savable yet → keep staged; keep its old baseline (if
                        // any) so it stays flagged dirty.
                        savedByWorkingId.set(row.id, row);
                        if (base) nextBaseline.push(base);
                        continue;
                    }
                    if (isTempId(row.id)) {
                        const created = await createLossRunApi(submissionId, toWritePayload(row), locations);
                        const saved = { ...row, id: created.id, totalIncurred: created.totalIncurred, createdAt: created.createdAt, updatedAt: created.updatedAt };
                        savedByWorkingId.set(row.id, saved);
                        nextBaseline.push(saved);
                        continue;
                    }
                    const diff = base ? diffPayload(base, row) : {};
                    if (Object.keys(diff).length > 0) {
                        const patched = await patchLossRunApi(submissionId, row.id, diff, locations);
                        const saved = { ...row, totalIncurred: patched.totalIncurred, updatedAt: patched.updatedAt };
                        savedByWorkingId.set(row.id, saved);
                        nextBaseline.push(saved);
                    } else {
                        savedByWorkingId.set(row.id, row);
                        nextBaseline.push(base ?? row);
                    }
                }

                // Re-list the rows + refresh the summary breakdown post-save.
                // The full list is the ONLY read that resolves each row's
                // `sir_by_community` (the POST/PATCH responses return it null —
                // the backend joins the location only on the list query,
                // OR-1337), and it also carries the recomputed card aggregates.
                // A transient failure falls back to the current values instead
                // of clobbering anything.
                const [listed, summary] = await Promise.all([
                    listLossRunsApi(submissionId).catch(() => null),
                    getLossRunSummaryApi(submissionId).catch(() => current.summary),
                ]);
                const aggregates = listed?.aggregates ?? current.aggregates;
                const serverById = new Map((listed?.rows ?? []).map((r) => [r.id, r]));

                // The user may have kept editing while the awaits above were in
                // flight (keystrokes mutate the store synchronously). Emitting
                // the `working` snapshot captured at entry would silently revert
                // those edits — QA saw a date typed during an in-flight autosave
                // snap back truncated right after Save. So merge at emit time:
                // keep each LIVE row's writable fields and adopt only the
                // server-owned fields from this save (id swap for creates,
                // totalIncurred, timestamps — plus every row's fresh
                // sirByCommunity from the re-list, since it can change without
                // the row itself being touched). Rows edited mid-save stay
                // dirty vs the persisted baseline, so the autosave re-flushes
                // them.
                const live = stateByKey.get(key);
                const liveRows = live?.status === "ready" ? live.data : working;
                const merged = liveRows.map((row) => {
                    const saved = savedByWorkingId.get(row.id);
                    let next = saved
                        ? { ...row, id: saved.id, totalIncurred: saved.totalIncurred, createdAt: saved.createdAt, updatedAt: saved.updatedAt }
                        : row; // row added while we were saving
                    const server = serverById.get(next.id);
                    // Adopt the server-derived read-only fields from the
                    // authoritative re-list: sirByCommunity (OR-1337) and
                    // operator (OR-1411, re-derived from the row's location_id).
                    if (server) next = { ...next, sirByCommunity: server.sirByCommunity, operator: server.operator };
                    return next;
                });
                const baselineNext = nextBaseline.map((b) => {
                    const server = serverById.get(b.id);
                    return server ? { ...b, sirByCommunity: server.sirByCommunity, operator: server.operator } : b;
                });
                baselineByKey.set(key, snapshot(baselineNext));
                emit(key, { status: "ready", data: merged, aggregates, locations, summary });
                return true;
            } catch (error) {
                console.warn("saveLossRuns failed:", error);
                return false;
            }
        };

        // Serialize saves per scope (see saveChainByKey). Chain even after a
        // failure so one rejected flush doesn't wedge the queue.
        const prev = saveChainByKey.get(key) ?? Promise.resolve(true);
        const chained = prev.then(run, run);
        saveChainByKey.set(key, chained);
        try {
            return await chained;
        } finally {
            if (saveChainByKey.get(key) === chained) saveChainByKey.delete(key);
        }
    }, [submissionId, key]);

    const baseline = key === null ? undefined : baselineByKey.get(key);

    // Derived dirty state — memoized so the scan runs once per actual
    // data/baseline change instead of on every render of every consumer.
    // This hook is shared by the page, the tabs, the summary cards AND the
    // editable table, so an unmemoized recompute on every keystroke (the
    // old per-row `.find` was O(n²) and `isDirty` JSON-stringified the whole
    // list twice) was a measurable cost. Per-row dirty set: a row is dirty
    // if it's a not-yet-POSTed temp row or any writable column differs from
    // the saved baseline. The table uses this to blank (—) backend-computed
    // columns until Save.
    const { isDirty, dirtyIds } = useMemo(() => {
        const ids = new Set<string>();
        if (state.status !== "ready" || baseline === undefined) {
            return { isDirty: false, dirtyIds: ids };
        }
        const baseById = new Map(baseline.map((b) => [b.id, b]));
        for (const row of state.data) {
            if (isTempId(row.id)) {
                ids.add(row.id);
                continue;
            }
            const b = baseById.get(row.id);
            if (!b || WRITABLE_KEYS.some((k) => b[k] !== row[k])) ids.add(row.id);
        }
        // Dirty when a row was added/edited (ids) or removed (length drop).
        // Reordering isn't possible in these tables, so length + per-row id
        // checks are equivalent to the old full JSON compare.
        const dirty = state.data.length !== baseline.length || ids.size > 0;
        return { isDirty: dirty, dirtyIds: ids };
    }, [state, baseline]);

    return { state, create, update, remove, save, isDirty, dirtyIds };
}
